import React, { useState } from 'react';
import { Icons } from '../lib/Icons';

/**
 * Resource Pack Detail Modal
 * Displays full pack information and allows downloading individual items or full pack
 */
const ResourcePackDetailModal = ({ 
  pack, 
  isOpen, 
  onClose, 
  onDownloadPack, 
  onDownloadItem,
  subscriptionStatus,
  user,
  downloadingPackId,
  downloadingItemId 
}) => {
  const [selectedItemId, setSelectedItemId] = useState(null);

  if (!isOpen || !pack) return null;

  const typeLabels = {
    'worksheet': 'Worksheet',
    'visual-support': 'Visual Support',
    'checklist': 'Checklist',
    'guide': 'Guide',
    'tracker': 'Tracker',
  };

  const typeIcons = {
    'worksheet': Icons.ClipboardDocumentList,
    'visual-support': Icons.Eye,
    'checklist': Icons.CheckCircle,
    'guide': Icons.BookOpen,
    'tracker': Icons.ChartBar || Icons.Clock,
  };

  const handleDownloadItem = async (itemId) => {
    setSelectedItemId(itemId);
    try {
      await onDownloadItem(pack.id, itemId);
    } finally {
      setSelectedItemId(null);
    }
  };

  const isDownloadingPack = downloadingPackId === pack.id;
  const canDownload = user && subscriptionStatus?.hasActiveSubscription;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-slate-800 rounded-2xl shadow-2xl border border-slate-700/50 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className={`bg-gradient-to-br ${pack.gradient} p-6 rounded-t-2xl`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {pack.phase && (
                    <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                      {pack.phase === 'primary' ? 'Primary' : 'Secondary'}
                    </span>
                  )}
                  <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                    {pack.worksheetCount} resources
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {pack.title}
                </h2>
                <p className="text-white/90 text-sm">
                  {pack.description}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                <Icons.XMark className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Pack Info */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-400 mb-2">Category</h3>
              <p className="text-white capitalize">{pack.category.replace('-', ' ')}</p>
              
              {pack.tags && pack.tags.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-slate-400 mb-2 mt-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {pack.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Contents List */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Icons.ClipboardDocumentList className="w-5 h-5 text-purple-400" />
                Pack Contents
              </h3>
              
              {pack.contents && pack.contents.length > 0 ? (
                <div className="space-y-3">
                  {pack.contents.map((item, index) => {
                    const ItemIcon = typeIcons[item.type] || Icons.Document;
                    const isDownloading = selectedItemId === item.id || downloadingItemId === item.id;
                    
                    return (
                      <div
                        key={item.id}
                        className="bg-slate-700/50 rounded-xl p-4 border border-slate-600/50 hover:border-purple-500/50 transition-all"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="p-2 rounded-lg bg-purple-500/20">
                                <ItemIcon className="w-4 h-4 text-purple-400" />
                              </div>
                              <div>
                                <h4 className="text-white font-semibold">
                                  {index + 1}. {item.title}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-slate-400">
                                    {typeLabels[item.type] || item.type}
                                  </span>
                                  <span className="text-xs text-slate-500">•</span>
                                  <span className="text-xs text-slate-400">
                                    {item.pageCount} page{item.pageCount !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p className="text-slate-300 text-sm mt-2">
                              {item.description}
                            </p>
                          </div>
                          
                          {canDownload ? (
                            <button
                              onClick={() => handleDownloadItem(item.id)}
                              disabled={isDownloading}
                              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                                isDownloading
                                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                                  : 'bg-purple-600 hover:bg-purple-700 text-white'
                              }`}
                            >
                              {isDownloading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                                  <span className="text-xs">Generating...</span>
                                </>
                              ) : (
                                <>
                                  <Icons.ArrowDownTray className="w-4 h-4" />
                                  <span className="text-xs">Download</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              disabled
                              className="px-4 py-2 rounded-lg bg-slate-600 text-slate-400 cursor-not-allowed flex items-center gap-2"
                            >
                              <Icons.LockClosed className="w-4 h-4" />
                              <span className="text-xs">Locked</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-400">No contents available.</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-slate-700/50 p-6 bg-slate-800/50">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-slate-400">
                {pack.contents?.length || 0} resources • {pack.contents?.reduce((sum, item) => sum + item.pageCount, 0) || 0} total pages
              </div>
              
              {canDownload ? (
                <button
                  onClick={() => onDownloadPack(pack.id)}
                  disabled={isDownloadingPack}
                  className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                    isDownloadingPack
                      ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${pack.gradient} text-white hover:opacity-90`
                  }`}
                >
                  {isDownloadingPack ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Preparing Download...
                    </>
                  ) : (
                    <>
                      <Icons.ArrowDownTray className="w-5 h-5" />
                      Download Full Pack
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => user ? window.location.href = '/solo/checkout' : window.location.href = '/signup/solo'}
                  className="px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all flex items-center gap-2"
                >
                  <Icons.LockClosed className="w-5 h-5" />
                  {user ? 'Subscribe to Download' : 'Sign Up to Access'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePackDetailModal;
